import * as React from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';

export interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: React.ReactNode;
  body?: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
  /** If set, the user must type this exact string to enable confirm. */
  typedConfirmation?: string;
  /** If true, a required reason textarea is shown and passed to onConfirm. */
  reasonRequired?: boolean;
  reasonPlaceholder?: string;
  onConfirm: (args: { reason: string }) => Promise<void> | void;
  submitting?: boolean;
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  body,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  destructive,
  typedConfirmation,
  reasonRequired = true,
  reasonPlaceholder = 'Why are you making this change? (required — written to audit log)',
  onConfirm,
  submitting,
}: ConfirmDialogProps) {
  const [reason, setReason] = React.useState('');
  const [typed, setTyped] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!open) {
      setReason('');
      setTyped('');
      setError(null);
    }
  }, [open]);

  const typedOk = !typedConfirmation || typed.trim() === typedConfirmation;
  const reasonOk = !reasonRequired || reason.trim().length > 0;
  const canConfirm = typedOk && reasonOk && !submitting;

  const handleConfirm = async () => {
    if (!canConfirm) return;
    try {
      await onConfirm({ reason: reason.trim() });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Operation failed');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        {body && <div>{body}</div>}
        {typedConfirmation && (
          <div className="space-y-1.5">
            <Label htmlFor="confirm-typed">
              Type <span className="font-mono text-destructive">{typedConfirmation}</span> to confirm
            </Label>
            <Input
              id="confirm-typed"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              autoComplete="off"
            />
          </div>
        )}
        {reasonRequired && (
          <div className="space-y-1.5">
            <Label htmlFor="confirm-reason">Reason (required)</Label>
            <Textarea
              id="confirm-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={reasonPlaceholder}
              maxLength={500}
              required
            />
            <p className="text-xs text-muted-foreground">
              {reason.length}/500 — written to audit log and visible to all admins.
            </p>
          </div>
        )}
        {error && <p className="text-sm text-destructive">{error}</p>}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            {cancelLabel}
          </Button>
          <Button
            variant={destructive ? 'destructive' : 'default'}
            onClick={handleConfirm}
            disabled={!canConfirm}
            className={cn(submitting && 'pointer-events-none opacity-70')}
          >
            {submitting ? 'Working…' : confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
